import { useEffect, useState, useCallback, useRef } from 'react';
import { spotifyApi } from '@/lib/axios/spotify';

interface SpotifyPlayer {
  connect: () => Promise<boolean>;
  disconnect: () => void;
  addListener: (event: string, callback: (data: any) => void) => boolean;
  removeListener: (event: string, callback?: (data: any) => void) => boolean;
  getCurrentState: () => Promise<SpotifyPlayerState | null>;
  setName: (name: string) => Promise<void>;
  getVolume: () => Promise<number>;
  setVolume: (volume: number) => Promise<void>;
  pause: () => Promise<void>;
  resume: () => Promise<void>;
  togglePlay: () => Promise<void>;
  seek: (positionMs: number) => Promise<void>;
  previousTrack: () => Promise<void>;
  nextTrack: () => Promise<void>;
}

interface SpotifyPlayerState {
  context: {
    uri: string;
    metadata: any;
  };
  disallows: {
    pausing: boolean;
    skipping_prev: boolean;
  };
  paused: boolean;
  position: number;
  duration: number;
  track_window: {
    current_track: {
      id: string;
      uri: string;
      name: string;
      album: {
        uri: string;
        name: string;
        images: Array<{ url: string }>;
      };
      artists: Array<{ uri: string; name: string }>;
    };
  };
}

interface SpotifyDevice {
  id: string;
  name: string;
  type: string;
  is_active: boolean;
  is_private_session: boolean;
  is_restricted: boolean;
  volume_percent: number;
}

interface UseSpotifyPlaybackReturn {
  player: SpotifyPlayer | null;
  deviceId: string | null;
  isReady: boolean;
  isPlaying: boolean;
  currentTrack: SpotifyPlayerState['track_window']['current_track'] | null;
  position: number;
  duration: number;
  volume: number;
  devices: SpotifyDevice[];
  activeDeviceId: string | null;
  connect: () => Promise<void>;
  disconnect: () => void;
  playTrack: (trackUri: string, targetDeviceId?: string) => Promise<void>;
  pause: () => Promise<void>;
  resume: () => Promise<void>;
  seek: (positionMs: number) => Promise<void>;
  setVolume: (volume: number) => Promise<void>;
  getVolume: () => Promise<number>;
  fetchDevices: () => Promise<SpotifyDevice[]>;
  switchDevice: (deviceId: string) => Promise<void>;
}

const STORAGE_KEY = 'spotify-playback-token';
const STORAGE_EXPIRY_KEY = 'spotify-playback-token-expires';
const DEVICE_ID_KEY = 'spotify-device-id';

// Module-level player singleton to persist across route changes
let globalPlayer: SpotifyPlayer | null = null;
let globalPlayerReady = false;

export function useSpotifyPlayback(accessToken?: string): UseSpotifyPlaybackReturn {
  const [player, setPlayer] = useState<SpotifyPlayer | null>(globalPlayer);
  const [deviceId, setDeviceId] = useState<string | null>(() => {
    // Load device ID from storage on init
    if (typeof window !== 'undefined') {
      return localStorage.getItem(DEVICE_ID_KEY);
    }
    return null;
  });
  const [isReady, setIsReady] = useState(globalPlayerReady);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTrack, setCurrentTrack] = useState<SpotifyPlayerState['track_window']['current_track'] | null>(null);
  const [position, setPosition] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolumeState] = useState(50); // 0-100
  const [devices, setDevices] = useState<SpotifyDevice[]>([]);
  const [activeDeviceId, setActiveDeviceId] = useState<string | null>(null);
  const positionIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isSeekingRef = useRef(false); // Track if user is currently seeking
  const sdkLoadedRef = useRef(false); // Track if SDK script already loaded

  // Load access token from storage if not provided
  const token = accessToken || (typeof window !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null);

  // Save token to storage
  useEffect(() => {
    if (accessToken && typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, accessToken);
      localStorage.setItem(STORAGE_EXPIRY_KEY, String(Date.now() + 3600 * 1000));
    }
  }, [accessToken]);

  // Sync state when mounting with existing player
  useEffect(() => {
    if (player && globalPlayerReady) {
      setIsReady(true);
      // Load device ID from storage
      const savedDeviceId = localStorage.getItem(DEVICE_ID_KEY);
      if (savedDeviceId) {
        setDeviceId(savedDeviceId);
      }
    }
  }, [player]);

  // Detect if something is already playing when token is available
  useEffect(() => {
    if (!token) return;

    const checkCurrentPlayback = async () => {
      try {
        const response = await spotifyApi.get('/me/player');

        // null data = 204 No Content = no active playback (expected)
        if (!response.data) {
          return;
        }

        const data = response.data;
        if (data && data.item) {
          setIsPlaying(data.is_playing);
          setPosition(data.progress_ms || 0);
          setCurrentTrack({
            id: data.item.id,
            uri: data.item.uri,
            name: data.item.name,
            album: data.item.album,
            artists: data.item.artists,
          });

          // Set active device
          if (data.device?.id) {
            setActiveDeviceId(data.device.id);
          }
        }
      } catch (error) {
        // Errors (including 401) are handled by axios interceptor
        console.error('Failed to check current playback:', error);
      }
    };

    // Check immediately when token is available
    checkCurrentPlayback();
  }, [token]);

  // Initialize Spotify Web Playback SDK
  useEffect(() => {
    if (!token) return;
    if (player) {
      return;
    }

    // Define the player initialization function FIRST
    const initializePlayer = () => {
      // @ts-ignore
      if (!window.Spotify) {
        console.error('Spotify SDK not loaded yet');
        return;
      }

      // @ts-ignore
      const spotifyPlayer = new window.Spotify.Player({
        name: 'Music Analyzer Player',
        getOAuthToken: (cb: (token: string) => void) => {
          cb(token);
        },
        volume: 0.5,
      });

      // Ready
      spotifyPlayer.addListener('ready', ({ device_id }: { device_id: string }) => {
        // Save device ID to storage for persistence
        localStorage.setItem(DEVICE_ID_KEY, device_id);
        setDeviceId(device_id);
        globalPlayerReady = true;
        setIsReady(true);
      });

      // Not Ready
      spotifyPlayer.addListener('not_ready', (_: { device_id: string }) => {
        globalPlayerReady = false;
        setIsReady(false);
      });

      // Player State Changed
      spotifyPlayer.addListener('player_state_changed', (state: SpotifyPlayerState) => {
        if (!state) return;

        setIsPlaying(!state.paused);
        setCurrentTrack(state.track_window.current_track);
        setPosition(state.position);
        setDuration(state.duration);
      });

      // Error handling
      spotifyPlayer.addListener('initialization_error', ({ message }: { message: string }) => {
        console.error('Initialization Error:', message);
      });

      spotifyPlayer.addListener('authentication_error', ({ message }: { message: string }) => {
        console.error('Authentication Error:', message);
      });

      spotifyPlayer.addListener('account_error', ({ message }: { message: string }) => {
        console.error('Account Error:', message);
      });

      spotifyPlayer.addListener('playback_error', ({ message }: { message: string }) => {
        console.error('Playback Error:', message);
      });

      // Store player globally so it persists across route changes
      globalPlayer = spotifyPlayer;
      setPlayer(spotifyPlayer);

      // Connect the player immediately after creation
      spotifyPlayer.connect().then((success: boolean) => {
        if (!success) {
          console.error('Spotify Player failed to connect');
        }
      });
    };

    // Check if SDK is already loaded (e.g., on client-side navigation)
    if (sdkLoadedRef.current || document.querySelector('script[src*="spotify-player.js"]')) {
      // SDK script already loaded
      if ((window as any).Spotify) {
        // SDK is loaded and ready - initialize immediately
        initializePlayer();
      } else {
        // SDK script is in DOM but not loaded yet - wait for callback
        // @ts-ignore
        window.onSpotifyWebPlaybackSDKReady = initializePlayer;
      }
      return;
    }

    // Load Spotify SDK script for the first time
    const script = document.createElement('script');
    script.src = 'https://sdk.scdn.co/spotify-player.js';
    script.async = true;
    document.body.appendChild(script);
    sdkLoadedRef.current = true;

    // @ts-ignore
    window.onSpotifyWebPlaybackSDKReady = initializePlayer;

    // Don't disconnect on unmount - keep player alive for navigation
    // Only clean up if token changes (handled by dependency array)
  }, [token]);

  // Update position while playing (works for both web player and desktop devices)
  useEffect(() => {
    if (!token) return;

    if (isPlaying) {
      positionIntervalRef.current = setInterval(async () => {
        try {
          // Fetch current playback state from Web API (works for all devices)
          const response = await spotifyApi.get('/me/player');
          const data = response.data;

          if (data && data.is_playing) {
            // Only update position if we're not currently seeking
            if (!isSeekingRef.current) {
              setPosition(data.progress_ms || 0);
            }
            setIsPlaying(true);

            // Update track info if it changed
            if (data.item && data.item.id !== currentTrack?.id) {
              setCurrentTrack({
                id: data.item.id,
                uri: data.item.uri,
                name: data.item.name,
                album: data.item.album,
                artists: data.item.artists,
              });
            }
          } else if (data && !data.is_playing) {
            setIsPlaying(false);
          }
        } catch (error) {
          // Errors (including 401) are handled by axios interceptor
          console.error('Failed to fetch playback state:', error);
        }
      }, 500); // Poll every 500ms for smoother updates
    } else {
      if (positionIntervalRef.current) {
        clearInterval(positionIntervalRef.current);
      }
    }

    return () => {
      if (positionIntervalRef.current) {
        clearInterval(positionIntervalRef.current);
      }
    };
  }, [isPlaying, token, currentTrack]);

  const connect = useCallback(async () => {
    if (player) {
      await player.connect();
    }
  }, [player]);

  const disconnect = useCallback(() => {
    if (player) {
      player.disconnect();
    }
  }, [player]);

  const fetchDevices = useCallback(async (): Promise<SpotifyDevice[]> => {
    if (!token) return [];

    try {
      const response = await spotifyApi.get('/me/player/devices');
      const deviceList = response.data?.devices || [];
      setDevices(deviceList);

      // Update active device
      const active = deviceList.find((d: SpotifyDevice) => d.is_active);
      if (active) {
        setActiveDeviceId(active.id);
      }

      return deviceList;
    } catch (error) {
      console.error('Failed to fetch devices:', error);
      return [];
    }
  }, [token]);

  const playTrack = useCallback(async (trackUri: string, targetDeviceId?: string) => {
    if (!token) return;

    try {
      // Use specified device, or active device, or fall back to web player device
      const useDeviceId = targetDeviceId || activeDeviceId || deviceId;

      const endpoint = useDeviceId
        ? `/me/player/play?device_id=${useDeviceId}`
        : '/me/player/play'; // Play on currently active device

      await spotifyApi.put(endpoint, {
        uris: [trackUri],
      });

      // Immediately update state to show playing
      setIsPlaying(true);

      // Fetch initial playback state after a short delay
      setTimeout(async () => {
        try {
          const response = await spotifyApi.get('/me/player');
          if (response.data) {
            setPosition(response.data.progress_ms || 0);
            setIsPlaying(response.data.is_playing);
          }
        } catch (error) {
          console.error('Failed to fetch playback state after play:', error);
        }
      }, 200);
    } catch (error) {
      console.error('Failed to play track:', error);
    }
  }, [deviceId, activeDeviceId, token]);

  const switchDevice = useCallback(async (targetDeviceId: string) => {
    if (!token) return;

    try {
      // Transfer playback to the specified device
      await spotifyApi.put('/me/player', {
        device_ids: [targetDeviceId],
        play: false, // Don't start playing automatically
      });

      setActiveDeviceId(targetDeviceId);
    } catch (error) {
      console.error('Failed to switch device:', error);
    }
  }, [token]);

  const pause = useCallback(async () => {
    if (!token) return;

    try {
      // Use Web API to pause (works on any device)
      await spotifyApi.put('/me/player/pause');
      setIsPlaying(false);
    } catch (error) {
      console.error('Failed to pause:', error);
    }
  }, [token]);

  const resume = useCallback(async () => {
    if (!token) return;

    try {
      // Use Web API to resume (works on any device)
      await spotifyApi.put('/me/player/play');
      setIsPlaying(true);
    } catch (error) {
      console.error('Failed to resume:', error);
    }
  }, [token]);

  const seek = useCallback(async (positionMs: number) => {
    if (!token) return;

    // Immediately update position (optimistic) - no flicker!
    const targetPosition = Math.floor(positionMs);
    setPosition(targetPosition);

    // Pause position polling while seeking
    isSeekingRef.current = true;

    try {
      // Send seek command to Spotify (works on any device)
      await spotifyApi.put(`/me/player/seek?position_ms=${targetPosition}`);

      // Wait longer for Spotify to process the seek before resuming polling
      setTimeout(() => {
        isSeekingRef.current = false;
      }, 800);
    } catch (error) {
      console.error('Failed to seek:', error);
      isSeekingRef.current = false;
    }
  }, [token]);

  const setVolume = useCallback(async (volumePercent: number) => {
    if (player) {
      // Spotify volume is 0.0 to 1.0, we use 0-100
      await player.setVolume(volumePercent / 100);
      setVolumeState(volumePercent);
    }
  }, [player]);

  const getVolume = useCallback(async () => {
    if (player) {
      const vol = await player.getVolume();
      // Convert 0.0-1.0 to 0-100
      const volumePercent = Math.round(vol * 100);
      setVolumeState(volumePercent);
      return volumePercent;
    }
    return volume;
  }, [player, volume]);

  // Get initial volume when player is ready
  useEffect(() => {
    if (player && isReady) {
      getVolume();
    }
  }, [player, isReady, getVolume]);

  return {
    player,
    deviceId,
    isReady,
    isPlaying,
    currentTrack,
    position,
    duration,
    volume,
    devices,
    activeDeviceId,
    connect,
    disconnect,
    playTrack,
    pause,
    resume,
    seek,
    setVolume,
    getVolume,
    fetchDevices,
    switchDevice,
  };
}
